// src/lib/deckApi.ts - Deck management API functions with deck locking

import { supabase } from './supabaseClient';
import { PlayerDeck, DeckChoice, DeckValidation, Card } from '../types/index';

export const deckApi = {
  // Check if a deck is currently in use by active games
  async isDeckInUse(userId: string, deckSlot: 1 | 2 | 3): Promise<{
    inUse: boolean;
    games: Array<{ game_id: number; opponent: string; status: string }>;
  }> {
    const deckChoice = `deck_${deckSlot}` as DeckChoice;
    
    
    // FIXED: Only select columns that actually exist in dd_games table
    const { data: games, error } = await supabase
      .from('dd_games')
      .select(`
        game_id,
        game_status,
        player1_id,
        player2_id,
        player1_deck_choice,
        player2_deck_choice
      `)
      .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
      .in('game_status', ['pending', 'active']);


    if (error) {
      return { inUse: false, games: [] };
    }

    // Filter games where this specific user is using this specific deck
    const relevantGames = (games || []).filter(game => {
      const isPlayer1 = game.player1_id === userId;
      const isPlayer2 = game.player2_id === userId;
      
      
      if (isPlayer1 && game.player1_deck_choice === deckChoice) {
        return true;
      }
      if (isPlayer2 && game.player2_deck_choice === deckChoice) {
        return true;
      }
      
      return false;
    });


    // Get opponent usernames by making a separate query
    const gameInfo = await Promise.all(relevantGames.map(async (game) => {
      const isPlayer1 = game.player1_id === userId;
      const opponentId = isPlayer1 ? game.player2_id : game.player1_id;
      
      // Get opponent username from profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('user_id', opponentId)
        .single();
      
      return {
        game_id: game.game_id,
        opponent: profile?.username || 'Unknown',
        status: game.game_status
      };
    }));

    return {
      inUse: relevantGames.length > 0,
      games: gameInfo
    };
  },

  // Check if any deck is in use (for bulk operations)
  async getDecksInUse(userId: string): Promise<{
    [key in 1 | 2 | 3]: {
      inUse: boolean;
      games: Array<{ game_id: number; opponent: string; status: string }>;
    }
  }> {
    const results = await Promise.all([
      this.isDeckInUse(userId, 1),
      this.isDeckInUse(userId, 2),
      this.isDeckInUse(userId, 3)
    ]);

    return {
      1: results[0],
      2: results[1],
      3: results[2]
    };
  },

  // Get all decks for a player with populated card data AND lock status
  async getPlayerDecks(userId: string): Promise<PlayerDeck[]> {
    const { data, error } = await supabase
      .from('dd_player_decks')
      .select('*')
      .eq('user_id', userId)
      .order('deck_slot');
    
    if (error) throw error;
    
    // Get deck usage status
    const deckUsage = await this.getDecksInUse(userId);
    
    // Populate card details for each deck
    const decksWithCards = await Promise.all(
      (data || []).map(async (deck) => {
        if (deck.card_ids && deck.card_ids.length > 0) {
          const { data: cards } = await supabase
            .from('dd_cards')
            .select('*')
            .in('id', deck.card_ids)
            .eq('active', true);
          
          return {
            ...deck,
            cards: cards || [],
            // Add lock status
            isLocked: deckUsage[deck.deck_slot as 1 | 2 | 3].inUse,
            lockInfo: deckUsage[deck.deck_slot as 1 | 2 | 3].games
          };
        }
        return {
          ...deck,
          cards: [],
          isLocked: deckUsage[deck.deck_slot as 1 | 2 | 3].inUse,
          lockInfo: deckUsage[deck.deck_slot as 1 | 2 | 3].games
        };
      })
    );
    
    return decksWithCards;
  },

  // Get a specific deck by slot
  async getPlayerDeck(userId: string, deckSlot: 1 | 2 | 3): Promise<PlayerDeck | null> {
    const { data, error } = await supabase
      .from('dd_player_decks')
      .select('*')
      .eq('user_id', userId)
      .eq('deck_slot', deckSlot)
      .single();
    
    if (error) return null;
    
    // Check if deck is locked
    const lockStatus = await this.isDeckInUse(userId, deckSlot);
    
    // Populate card details
    if (data.card_ids && data.card_ids.length > 0) {
      const { data: cards } = await supabase
        .from('dd_cards')
        .select('*')
        .in('id', data.card_ids)
        .eq('active', true);
      
      data.cards = cards || [];
    } else {
      data.cards = [];
    }
    
    // Add lock information
    data.isLocked = lockStatus.inUse;
    data.lockInfo = lockStatus.games;
    
    return data;
  },

  // Create or update a deck with lock validation
  async saveDeck(userId: string, deckSlot: 1 | 2 | 3, deckName: string, cardIds: number[]): Promise<{
    success: boolean;
    error?: string;
    deck?: PlayerDeck;
  }> {
    try {
      // FIRST: Check if deck is locked
      const lockStatus = await this.isDeckInUse(userId, deckSlot);
      if (lockStatus.inUse) {
        const gameList = lockStatus.games.map(g => `vs ${g.opponent} (${g.status})`).join(', ');
        return { 
          success: false, 
          error: `Deck ${deckSlot} is locked - currently used in: ${gameList}` 
        };
      }

      // Validate deck
      const validation = await this.validateDeck(userId, cardIds);
      if (!validation.isValid) {
        return { success: false, error: validation.errors.join(', ') };
      }

      // Limit to 10 cards
      const limitedCardIds = cardIds.slice(0, 10);

      const { data, error } = await supabase
        .from('dd_player_decks')
        .upsert({
          user_id: userId,
          deck_slot: deckSlot,
          deck_name: deckName || `Deck ${deckSlot}`,
          card_ids: limitedCardIds
        }, {
          onConflict: 'user_id,deck_slot'
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: 'Failed to save deck' };
      }

      return { success: true, deck: data };

    } catch (error) {
      return { success: false, error: 'Server error' };
    }
  },

  // Validate deck composition
  async validateDeck(userId: string, cardIds: number[]): Promise<DeckValidation> {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check card count
    if (cardIds.length > 10) {
      errors.push('Deck cannot have more than 10 cards');
    }

    if (cardIds.length === 0) {
      warnings.push('Empty deck - you can add up to 10 cards');
    }

    // Verify player owns all cards
    if (cardIds.length > 0) {
      const { data: playerCards } = await supabase
        .from('dd_player_cards')
        .select('card_id')
        .eq('user_id', userId)
        .in('card_id', cardIds);

      const ownedCardIds = new Set(playerCards?.map(pc => pc.card_id) || []);
      const unownedCards = cardIds.filter(id => !ownedCardIds.has(id));

      if (unownedCards.length > 0) {
        errors.push(`You don't own ${unownedCards.length} of these cards`);
      }
    }

    // Check for duplicates
    const uniqueCards = new Set(cardIds);
    if (uniqueCards.size !== cardIds.length) {
      errors.push('Deck cannot contain duplicate cards');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  },

  // Initialize default decks for new users
  async initializePlayerDecks(userId: string): Promise<void> {
    const defaultDecks = [
      { deck_slot: 1, deck_name: 'Deck 1', card_ids: [] },
      { deck_slot: 2, deck_name: 'Deck 2', card_ids: [] },
      { deck_slot: 3, deck_name: 'Deck 3', card_ids: [] }
    ];

    for (const deck of defaultDecks) {
      await supabase
        .from('dd_player_decks')
        .upsert({
          user_id: userId,
          deck_slot: deck.deck_slot as 1 | 2 | 3,
          deck_name: deck.deck_name,
          card_ids: deck.card_ids
        }, {
          onConflict: 'user_id,deck_slot'
        });
    }
  },

  // Get cards for a specific deck choice during game
  async getDeckCards(userId: string, deckChoice: DeckChoice): Promise<Card[]> {
    let deckSlot: 1 | 2 | 3;

    if (deckChoice === 'random') {
      // Randomly select one of the three decks
      deckSlot = (Math.floor(Math.random() * 3) + 1) as 1 | 2 | 3;
    } else {
      // Extract deck number from choice (deck_1 -> 1)
      deckSlot = parseInt(deckChoice.split('_')[1]) as 1 | 2 | 3;
    }

    const deck = await this.getPlayerDeck(userId, deckSlot);
    return deck?.cards || [];
  },

  // Delete a deck (reset to empty) with lock validation
  async clearDeck(userId: string, deckSlot: 1 | 2 | 3): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // FIRST: Check if deck is locked
      const lockStatus = await this.isDeckInUse(userId, deckSlot);
      if (lockStatus.inUse) {
        const gameList = lockStatus.games.map(g => `vs ${g.opponent} (${g.status})`).join(', ');
        return { 
          success: false, 
          error: `Cannot clear Deck ${deckSlot} - currently used in: ${gameList}` 
        };
      }

      const { error } = await supabase
        .from('dd_player_decks')
        .update({
          card_ids: [],
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('deck_slot', deckSlot);

      if (error) {
        return { success: false, error: 'Failed to clear deck' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Server error' };
    }
  }
};